import { injectable } from 'inversify';
import Axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';

interface Config {
  baseUrl: string;
  apiSSOUrl: string;
  clientApiUrl: string;
  getUserTokens: () => Promise<{ refresh: any; access: any }>;
  handleSignIn: () => void;
}

export enum INSTANCES {
  API_INSTANCE = 'API_INSTANCE',
  SSO_INSTANCE = 'SSO_INSTANCE',
  CLIENT_API_INSTANCE = 'CLIENT_API_INSTANCE',
}

@injectable()
export default class HttpClient {
  static diToken = Symbol('httpClient');

  private getAccessToken!: Config['getUserTokens'];
  private config?: Config;

  // private instance: AxiosInstance;
  private instances: AxiosInstance[] = [];

  initialize(config: Config) {
    // Default axios
    Axios.defaults.baseURL = config.baseUrl;

    // Additional API instances
    this.instances[INSTANCES.API_INSTANCE] = Axios.create({
      baseURL: config.baseUrl,
    });

    this.instances[INSTANCES.SSO_INSTANCE] = Axios.create({
      baseURL: config.apiSSOUrl,
    });

    this.instances[INSTANCES.CLIENT_API_INSTANCE] = Axios.create({
      baseURL: config.clientApiUrl,
    });

    this.getAccessToken = config.getUserTokens;
    this.config = config;

    this.configureInstances();
  }

  private configureInstances() {
    Axios.defaults.headers.common['Content-Type'] = 'application/json';

    this.instances[INSTANCES.API_INSTANCE].defaults.headers.common['Content-Type'] =
      'application/json';

    this.instances[INSTANCES.SSO_INSTANCE].defaults.headers.common['Content-Type'] =
      'application/json';

    this.instances[INSTANCES.CLIENT_API_INSTANCE].defaults.headers.common['Content-Type'] =
      'application/json';

    this.setRequestInterceptors();
    this.setResponseInterceptors();
  }

  private async getAuthHeader() {
    const tokens = await this.getAccessToken();

    return tokens.access ? `Bearer ${tokens.access}` : '';
  }

  private setRequestInterceptors() {
    Axios.interceptors.request.use(
      async (config: InternalAxiosRequestConfig): Promise<InternalAxiosRequestConfig> => {
        const authHeader = await this.getAuthHeader();

        if (authHeader) {
          config.headers.set('Authorization', authHeader);
        }

        return config;
      },
      (error: Error): Error => {
        throw error;
      },
    );

    const setPerInstance = (instance: INSTANCES) => {
      this.instances[instance].interceptors.request.use(
        async (config: InternalAxiosRequestConfig): Promise<InternalAxiosRequestConfig> => {
          const authHeader = await this.getAuthHeader();

          if (authHeader) {
            config.headers.set('Authorization', authHeader);
          }

          return config;
        },
        (error: Error): Error => {
          throw error;
        },
      );
    };

    setPerInstance(INSTANCES.API_INSTANCE);
    setPerInstance(INSTANCES.SSO_INSTANCE);
    setPerInstance(INSTANCES.CLIENT_API_INSTANCE);
  }

  private setResponseInterceptors() {
    Axios.interceptors.response.use(
      response => ({ ...response, data: response.data.data || response.data }),
      async error => {
        const response = error ? error.response : undefined;

        if (!response) {
          return;
        }

        if (response.status === 403) {
          this.processForbidden();
        }

        if (response.status === 401) {
          this.config?.handleSignIn();
        }

        if (response.status >= 500) {
          // TODO: show message
        }

        throw error;
      },
    );

    const setPerInstance = (instance: INSTANCES) => {
      this.instances[instance].interceptors.response.use(
        response => ({ ...response, data: response.data.data || response.data }),
        (error: AxiosError) => {
          const response = error ? error.response : undefined;

          if (response?.status === 401) {
            this.config?.handleSignIn();
          }

          throw error;
        },
      );
    };

    setPerInstance(INSTANCES.API_INSTANCE);
    setPerInstance(INSTANCES.CLIENT_API_INSTANCE);
  }

  private processForbidden(msg = '', showMessage = true) {
    if (showMessage) {
      // showNotification(msg);
    }
  }

  getInstance(type: INSTANCES = INSTANCES.API_INSTANCE) {
    return this.instances[type];
  }
}
